import { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { activeMenuPage, breadcrumbPageHref, detailLabel, mainMenu, ownsPage, type NavigationAccess } from "../lib/appNavigation";
import { useSession } from "../auth/useSession";

/**
 * What this person may see (#538). Undefined while the profile is loading, so
 * the menu waits instead of showing a smaller one first.
 */
function useNavigationAccess(): NavigationAccess | undefined {
  const { user } = useSession();
  return user?.active_membership;
}

/** Hauptmenü unter der Kopfzeile (#630); auf schmalen Bildschirmen seitlich scrollbar. */
export function MainMenu({ counts = {} }: { counts?: Record<string, number> }) {
  const { pathname, search } = useLocation();
  const access = useNavigationAccess();
  const activeRef = useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    // Scroll only the menu strip; never move the page.
    const link = activeRef.current;
    const strip = link?.parentElement;
    if (link && strip) strip.scrollLeft = Math.max(0, link.offsetLeft - strip.offsetLeft - 16);
  }, [pathname]);
  const pages = mainMenu(access);
  if (pages.length === 0) return null;
  return (
    <div className="border-b border-slate-200 bg-white">
      <nav aria-label="Hauptnavigation" className="mx-auto flex max-w-7xl gap-1 overflow-x-auto px-2 text-sm sm:px-4">
        {pages.map(page => {
          const active = ownsPage(page, pathname, search);
          const count = counts[page.label];
          return (
            <Link key={page.to} to={page.to} ref={active ? activeRef : undefined}
              aria-current={active ? "page" : undefined}
              className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2.5 font-medium ${active ? "border-emerald-600 text-slate-950" : "border-transparent text-slate-600 hover:text-slate-900"}`}>
              {page.label}
              {count ? <span className="rounded-full bg-slate-100 px-1.5 text-[11px] font-semibold tabular-nums text-slate-700">{count.toLocaleString("de-AT")}</span> : null}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}

/** Reiter einer zusammengelegten Seite (Bestellungen, Belege hochladen). */
export function HubTabs({ label, tabs }: { label: string; tabs: { label: string; to: string; active: boolean; count?: number }[] }) {
  return (
    <div className="mx-auto max-w-7xl px-6 pt-3">
      <nav aria-label={label} className="flex gap-6 overflow-x-auto border-b border-slate-200 text-sm">
        {tabs.map(tab => (
          <Link key={tab.to} to={tab.to} aria-current={tab.active ? "page" : undefined}
            className={`-mb-px flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 py-2 font-medium ${tab.active ? "border-emerald-600 text-slate-950" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {tab.label}
            {tab.count ? <span className="rounded-full bg-slate-100 px-1.5 text-[11px] font-semibold tabular-nums text-slate-700">{tab.count}</span> : null}
          </Link>
        ))}
      </nav>
    </div>
  );
}

export function NavigationBreadcrumb() {
  const { pathname, search, state } = useLocation();
  const page = activeMenuPage(pathname, search);
  const detail = detailLabel(pathname, search);
  if (!page || !detail) return null;
  return (
    <nav aria-label="Seitenpfad" className="mx-auto w-full max-w-7xl px-6 pt-5 text-sm text-slate-600">
      <ol className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <li><Link to={breadcrumbPageHref(page, search, (state as { from?: unknown } | null)?.from)} className="hover:underline">{page.label}</Link></li>
        <li aria-hidden="true">›</li>
        <li aria-current="page" className="font-medium text-slate-900">{detail}</li>
      </ol>
    </nav>
  );
}
